import { mapFromActivities } from 'mappings/activityMapping';
import {
  mapFromDashboard,
  mapFromDashboards,
  mapFromDashboardScene,
  mapFromNormCoverage,
  mapFromTaskCompletedDeadlineStat,
  mapFromUserTaskStats,
  mapFromWidget,
  mapFromWidgets,
  mapToDashboard,
  mapToWidget,
} from 'mappings/dashboardMapping';
import { mapFromKPIGraphData } from 'mappings/kpiMapping';
import { mapFromResourceLinks, mapToResourceLink } from 'mappings/linkMapping';
import { mapFromRiskStates } from 'mappings/riskMapping';
import { mapFromTags } from 'mappings/tagMapping';
import { mapFromTaskStates } from 'mappings/taskMapping';
import Activity, { ActivityType } from 'models/activity';
import Dashboard, { DashboardTypes } from 'models/dashboard';
import DashboardScene, { NormCoverage, TaskCompletedDeadlineStat, UserTaskStat } from 'models/dashboardScene';
import ActivityDTO from 'models/dto/activityDTO';
import DashboardDTO from 'models/dto/dashboardDTO';
import DashboardSceneDTO, { NormCoverageDTO } from 'models/dto/dashboardSceneDTO';
import IdListDTO from 'models/dto/IdListDTO';
import KPIGraphDataDTO, { KPIGraphDataConfigDTO } from 'models/dto/kpi/kpiGraphDataDTO';
import ResourceLinkDTO from 'models/dto/resourceLinkDTO';
import RiskOwnerDTO from 'models/dto/riskOwnerDTO';
import RiskStateDTO from 'models/dto/riskStateDTO';
import TagDTO from 'models/dto/tagDTO';
import { TaskStateDTO } from 'models/dto/tasks/taskDTO';
import WidgetDTO from 'models/dto/widgetDTO';
import { Widget59DTO, Widget59ResultDTO } from 'models/dto/widgets/widget59DTO';
import { Widget61DTO } from 'models/dto/widgets/widget61DTO';
import { Widget63DTO, WidgetLibraryReadStatsConfigViewType } from 'models/dto/widgets/widget63DTO';
import { Widget67DTO, Widget67ResultDTO } from 'models/dto/widgets/widget67DTO';
import { Widget70DTO } from 'models/dto/widgets/widget70DTO';
import { Widget71DTO, Widget71ResultDTO } from 'models/dto/widgets/widget71DTO';
import { ListsPerTenant, Widget72DTO, Widget72ResultDTO } from 'models/dto/widgets/widget72DTO';
import { Widget73DTO, Widget73ResultDTO } from 'models/dto/widgets/widget73DTO';
import { Widget74DTO, Widget74ResultDTO } from 'models/dto/widgets/widget74DTO';
import { Widget91DTO } from 'models/dto/widgets/widget91DTO';
import { WidgetTenantOverviewDTO } from 'models/dto/widgets/widgetTenantOverviewDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import KPIGraphData from 'models/kpi/kpiGraphData';
import ResourceLink from 'models/resourceLink';
import RiskState from 'models/riskState';
import Tag from 'models/tag';
import { TaskState } from 'models/tasks/taskHelperClasses';
import AppError from 'utils/appError';
import { AuditTrailActions } from 'utils/auditTrailActions';
import { toApiDateOptional } from 'utils/datetime';
import Widget, { WidgetTypes } from './../../models/widget';
import http from './httpService';

export async function apiGetDashboardScene(
  accessToken: string,
  cache: GlobalDataCache,
  signal?: AbortSignal,
): Promise<DashboardScene> {
  try {
    const ar = await http.get<DashboardSceneDTO>(
      `/dashboards`,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return mapFromDashboardScene(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetActivitiesForDashboard(
  activityTypes: ActivityType[],
  skip: number,
  count: number,
  accessToken: string,
): Promise<Activity[]> {
  try {
    const idListDto = new IdListDTO(activityTypes.map((s) => s.toString()));
    const ar = await http.post<ActivityDTO[]>(
      `/dashboards/activities?skip=${skip}&count=${count}`,
      idListDto,
      http.getRequestConfig(accessToken),
    );

    return mapFromActivities(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiDismissActivity(activityId: number, accessToken: string): Promise<void> {
  try {
    await http.post(`/activities/dismiss/${activityId}`, null, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiDismissAllActivities(activityTypes: ActivityType[], accessToken: string): Promise<void> {
  try {
    const idListDto = new IdListDTO(activityTypes.map((s) => s.toString()));
    await http.post(`/activities/dismiss/all`, idListDto, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetDashboardsForType(
  type: DashboardTypes,
  accessToken: string,
  cache: GlobalDataCache,
  signal?: AbortSignal,
): Promise<Dashboard[]> {
  try {
    const ar = await http.get<DashboardDTO[]>(
      `/dashboards/type/${type}`,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    if (!ar || !ar.data || ar.data.length === 0) {
      return [];
    }

    return mapFromDashboards(ar.data, cache);
  } catch (err) {
    const appErr = AppError.fromApiError(err);
    if (appErr.debug?.includes('Authorization_IdentityNotFound')) {
      //this happens when the app cannot retrieve group membership
      //this can happen when the app is not authorized yet
      return [];
    } else {
      throw appErr;
    }
  }
}

export async function apiGetDashboardWidgets(
  dashboardId: number,
  accessToken: string,
  cache: GlobalDataCache,
  signal?: AbortSignal,
): Promise<Widget[]> {
  try {
    const ar = await http.get<WidgetDTO[]>(
      `/dashboards/${dashboardId}/widgets`,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return mapFromWidgets(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddDashboard(
  dashboard: Dashboard,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Dashboard> {
  try {
    const dashboardDTO = mapToDashboard(dashboard);
    const ar = await http.post(`/dashboards`, dashboardDTO, http.getRequestConfig(accessToken));

    return mapFromDashboard(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiDeleteDashboard(dashboard: Dashboard, accessToken: string): Promise<void> {
  try {
    await http.post(`/dashboards/delete/${dashboard.dashboardId}`, null, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateDashboard(
  dashboard: Dashboard,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Dashboard> {
  try {
    const dashboardDTO = mapToDashboard(dashboard);
    const ar = await http.put<DashboardDTO>(`/dashboards`, dashboardDTO, http.getRequestConfig(accessToken));

    return mapFromDashboard(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddWidgetToDashboard(
  widget: Widget,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Widget> {
  try {
    const widgetDTO = mapToWidget(widget);
    const ar = await http.post<WidgetDTO>(`/widgets`, widgetDTO, http.getRequestConfig(accessToken));

    return mapFromWidget(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateWidgetOnDashboard(
  widget: Widget,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Widget> {
  try {
    const widgetDTO = mapToWidget(widget);
    const ar = await http.put<WidgetDTO>(`/widgets`, widgetDTO, http.getRequestConfig(accessToken));

    return mapFromWidget(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveWidgetFromDashboard(
  widget: Widget,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<void> {
  try {
    await http.post(`/widgets/delete/${widget.widgetId}`, null, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataNormCoverage(
  isoNormId: number,
  tags: Tag[],
  accessToken: string,
  signal?: AbortSignal,
): Promise<NormCoverage> {
  try {
    const filterStr = `tags@${tags.map((t) => t.tagId.toString()).join(',')}`;
    const ar = await http.get<NormCoverageDTO>(
      `/widgets/${WidgetTypes.NormCoverage}/data/${isoNormId}?filter=${filterStr}`,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return mapFromNormCoverage(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataTenantControlStatus(
  tenants: string[],
  isoNormId: number,
  accessToken: string,
  signal?: AbortSignal,
): Promise<NormCoverage[] | undefined> {
  try {
    const dto = new Widget70DTO();
    dto.tenants = new IdListDTO(tenants);
    dto.isoNormId = isoNormId;

    const ar = await http.post<Widget70DTO>(
      `/widgets/${WidgetTypes.TenantControlStatus}/data`,
      dto,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return ar.data?.coverages?.map((n) => mapFromNormCoverage(n));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataNormCoverageForUser(
  isoNormId: number,
  tags: Tag[],
  userId: string | undefined,
  accessToken: string,
  signal?: AbortSignal,
): Promise<NormCoverage> {
  try {
    const filterStr = `tags@${tags.map((t) => t.tagId.toString()).join(',')}`;
    const ar = await http.get<NormCoverageDTO>(
      `/widgets/${WidgetTypes.NormCoverage}/data/${isoNormId}/user/${userId}?filter=${filterStr}`,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return mapFromNormCoverage(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataCustomNormCoverage(
  normId: number,
  tags: Tag[],
  accessToken: string,
  signal?: AbortSignal,
): Promise<NormCoverage> {
  try {
    const filterStr = `tags@${tags.map((t) => t.tagId.toString()).join(',')}`;
    const ar = await http.get<NormCoverageDTO>(
      `/widgets/${WidgetTypes.CustomNormCoverage}/data/${normId}?filter=${filterStr}`,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return mapFromNormCoverage(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataCustomNormCoverageForUser(
  normId: number,
  tags: Tag[],
  userId: string | undefined,
  accessToken: string,
  signal?: AbortSignal,
): Promise<NormCoverage> {
  try {
    const filterStr = `tags@${tags.map((t) => t.tagId.toString()).join(',')}`;
    const ar = await http.get<NormCoverageDTO>(
      `/widgets/${WidgetTypes.CustomNormCoverage}/data/${normId}/user/${userId}?filter=${filterStr}`,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return mapFromNormCoverage(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataTenantUserStats(
  tenants: string[],
  accessToken: string,
  signal?: AbortSignal,
): Promise<Widget74ResultDTO[]> {
  try {
    const dto = new Widget74DTO();
    dto.tenants = new IdListDTO(tenants);
    const ar = await http.post<Widget74ResultDTO[]>(
      `/widgets/${WidgetTypes.TenantUserStatsOrgUnit}/data/`,
      dto,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataTenantOverview(accessToken: string): Promise<WidgetTenantOverviewDTO> {
  try {
    //single-tenant overview; scoped to the customer via the customerid header (no tenant list needed)
    const ar = await http.get<WidgetTenantOverviewDTO>(
      `/widgets/${WidgetTypes.TenantOverview}/data`,
      http.getRequestConfig(accessToken),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataOpenTasksPerUser(
  tags: Tag[],
  overDeadline: boolean,
  start: Date | undefined,
  end: Date | undefined,
  accessToken: string,
  signal?: AbortSignal,
  projectId?: number,
): Promise<UserTaskStat[]> {
  try {
    let filterStr = '';
    if (tags.length > 0) filterStr += `tags@${tags.map((t) => t.tagId.toString()).join(',')}`;
    if (overDeadline) filterStr += (filterStr.length > 0 ? ';' : '') + 'overDeadline';
    if (start) filterStr += (filterStr.length > 0 ? ';' : '') + `start@${toApiDateOptional(start)}`;
    if (end) filterStr += (filterStr.length > 0 ? ';' : '') + `end@${toApiDateOptional(end)}`;
    if (projectId) filterStr += (filterStr.length > 0 ? ';' : '') + `project@${projectId}`;

    const ar = await http.get<UserTaskStat[]>(
      `/widgets/${WidgetTypes.OpenTasksPerUser}/data?filter=${filterStr}`,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return mapFromUserTaskStats(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetTenantTaskStatus(
  tenants: string[],
  tags: Tag[],
  useAndForTagGroups: boolean,
  overDeadline: boolean,
  start: Date | undefined,
  end: Date | undefined,
  accessToken: string,
  signal?: AbortSignal,
): Promise<Widget73ResultDTO[]> {
  try {
    let filterStr = '';
    //Identify the tags by name and value, not by id: this widget queries several tenants and a tag id is only
    //meaningful in one of them. For a partner the ids are a customer's, which resolve to nothing anywhere the
    //caller can see, and the whole tag filter would silently disappear.
    if (tags.length > 0) filterStr += `tagKeys@${tags.map((t) => `${t.tagName}^${t.tagValue}`).join(',')}`;
    if (useAndForTagGroups) filterStr += ';useAndForTagGroups';
    if (overDeadline) filterStr += (filterStr.length > 0 ? ';' : '') + 'overDeadline';
    if (start) filterStr += (filterStr.length > 0 ? ';' : '') + `start@${toApiDateOptional(start)}`;
    if (end) filterStr += (filterStr.length > 0 ? ';' : '') + `end@${toApiDateOptional(end)}`;

    const dto = new Widget73DTO();
    dto.tenants = new IdListDTO(tenants);
    dto.filter = filterStr;

    const ar = await http.post<Widget73ResultDTO[]>(
      `/widgets/${WidgetTypes.TenantTaskStatusOrgUnit}/data?filter=${filterStr}`,
      dto,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataTaskTag(
  tags: Tag[],
  showOpenTasks: boolean,
  start: Date | undefined,
  end: Date | undefined,
  accessToken: string,
  signal?: AbortSignal,
): Promise<Tag[]> {
  try {
    let filterStr = `tags@${tags.map((t) => t.tagId.toString()).join(',')}${showOpenTasks ? ';taskStatus@1,2,5' : ''}`;
    if (start) filterStr += `;start@${toApiDateOptional(start)}`;
    if (end) filterStr += `;end@${toApiDateOptional(end)}`;
    const ar = await http.get<TagDTO[]>(
      `/widgets/${WidgetTypes.TaskTag}/data?filter=${filterStr}`,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return mapFromTags(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataTaskStatus(
  tags: Tag[],
  useAndForTagGroups: boolean,
  start: Date | undefined,
  end: Date | undefined,
  accessToken: string,
  signal?: AbortSignal,
  projectId?: number,
): Promise<TaskState[]> {
  try {
    let filterStr = `tags@${tags.map((t) => t.tagId.toString()).join(',')}`;
    if (useAndForTagGroups) filterStr += ';useAndForTagGroups';
    if (start) filterStr += `;start@${toApiDateOptional(start)}`;
    if (end) filterStr += `;end@${toApiDateOptional(end)}`;
    if (projectId) filterStr += `;project@${projectId}`;
    const ar = await http.get<TaskStateDTO[]>(
      `/widgets/${WidgetTypes.TaskStatus}/data?filter=${filterStr}`,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return mapFromTaskStates(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//start and end apply to the completion date of the tasks
export async function apiGetWidgetDataTaskCompletedDeadline(
  tags: Tag[],
  useAndForTagGroups: boolean,
  start: Date | undefined,
  end: Date | undefined,
  accessToken: string,
  signal?: AbortSignal,
  projectId?: number,
): Promise<TaskCompletedDeadlineStat> {
  try {
    let filterStr = `tags@${tags.map((t) => t.tagId.toString()).join(',')}`;
    if (useAndForTagGroups) filterStr += ';useAndForTagGroups';
    if (start) filterStr += `;start@${toApiDateOptional(start)}`;
    if (end) filterStr += `;end@${toApiDateOptional(end)}`;
    if (projectId) filterStr += `;project@${projectId}`;
    const ar = await http.get<Widget91DTO>(
      `/widgets/${WidgetTypes.TaskCompletedDeadline}/data?filter=${filterStr}`,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return mapFromTaskCompletedDeadlineStat(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataRiskStatus(
  tags: Tag[],
  filterGoalBelowCurrentNoOpenControls: boolean,
  accessToken: string,
  signal?: AbortSignal,
): Promise<RiskState[]> {
  try {
    const filterStr = `tags@${tags.map((t) => t.tagId.toString()).join(',')}`;
    const ar = await http.get<RiskStateDTO[]>(
      `/widgets/${WidgetTypes.RiskStatus}/data?filterGoalBelowCurrentNoOpenControls=${filterGoalBelowCurrentNoOpenControls}&filter=${filterStr}`,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return mapFromRiskStates(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataRiskOwner(
  tags: Tag[],
  accessToken: string,
  signal?: AbortSignal,
): Promise<RiskOwnerDTO[]> {
  try {
    const filterStr = `tags@${tags.map((t) => t.tagId.toString()).join(',')}`;
    const ar = await http.get<RiskOwnerDTO[]>(
      `/widgets/${WidgetTypes.RiskOwner}/data?filter=${filterStr}`,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataTenantRiskStatus(
  tenants: string[],
  filterGoalBelowCurrentNoOpenControls: boolean,
  accessToken: string,
  signal?: AbortSignal,
): Promise<RiskState[]> {
  try {
    const ar = await http.post<RiskStateDTO[]>(
      `/widgets/${WidgetTypes.TenantRiskStatusOrgUnit}/data?filterGoalBelowCurrentNoOpenControls=${filterGoalBelowCurrentNoOpenControls}`,
      new IdListDTO(tenants),
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return mapFromRiskStates(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataTenantRiskOwner(
  tenants: string[],
  accessToken: string,
  signal?: AbortSignal,
): Promise<RiskOwnerDTO[]> {
  try {
    const ar = await http.post<RiskOwnerDTO[]>(
      `/widgets/${WidgetTypes.TenantRiskOwnerOrgUnit}/data`,
      new IdListDTO(tenants),
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataTenantRiskChanges(
  tenants: string[],
  actions: AuditTrailActions[],
  accessToken: string,
  signal?: AbortSignal,
): Promise<Widget67ResultDTO[]> {
  try {
    const dto = new Widget67DTO();
    dto.tenants = new IdListDTO(tenants);
    dto.actions = actions;

    const ar = await http.post<Widget67DTO>(
      `/widgets/${WidgetTypes.TenantRiskChangesOrgUnit}/data`,
      dto,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return ar.data?.results ?? [];
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataTenantControlChanges(
  tenants: string[],
  actions: AuditTrailActions[],
  accessToken: string,
  signal?: AbortSignal,
): Promise<Widget71ResultDTO[]> {
  try {
    const dto = new Widget71DTO();
    dto.tenants = new IdListDTO(tenants);
    dto.actions = actions;

    const ar = await http.post<Widget71DTO>(
      `/widgets/${WidgetTypes.TenantControlChangesOrgUnit}/data`,
      dto,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return ar.data?.results ?? [];
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataTenantListApprovalStatus(
  lists: ListsPerTenant,
  accessToken: string,
  signal?: AbortSignal,
): Promise<Widget72ResultDTO[]> {
  try {
    const dto = new Widget72DTO();
    dto.lists = lists;

    const ar = await http.post<Widget72DTO>(
      `/widgets/${WidgetTypes.TenantListApprovalStatusOrgUnit}/data`,
      dto,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return ar.data?.results ?? [];
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataSecureScores(
  accessToken: string,
  subscriptionId: string | undefined,
  microsoft365SecureScore: boolean,
  microsoftCloudSecureScore: boolean,
  microsoftDevicesSecureScore: boolean,
  microsoftIdentitySecureScore: boolean,
  signal?: AbortSignal,
): Promise<Widget59ResultDTO | undefined> {
  try {
    const dto = new Widget59DTO();
    dto.subscriptionId = subscriptionId;
    dto.microsoft365SecureScore = microsoft365SecureScore;
    dto.microsoftCloudSecureScore = microsoftCloudSecureScore;
    dto.microsoftDevicesSecureScore = microsoftDevicesSecureScore;
    dto.microsoftIdentitySecureScore = microsoftIdentitySecureScore;

    const ar = await http.post<Widget59DTO>(
      `/widgets/${WidgetTypes.MicrosoftSecureScores}/data`,
      dto,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return ar?.data?.result;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataSingleKPI(
  graphConfig: KPIGraphDataConfigDTO,
  accessToken: string,
  signal?: AbortSignal,
): Promise<KPIGraphData> {
  try {
    const ar = await http.post<KPIGraphDataDTO>(
      `/widgets/${WidgetTypes.SingleKPI}/data`,
      graphConfig,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return mapFromKPIGraphData(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataSingleKPILastPeriod(
  graphConfig: KPIGraphDataConfigDTO,
  accessToken: string,
  signal?: AbortSignal,
): Promise<KPIGraphData> {
  try {
    const ar = await http.post<KPIGraphDataDTO>(
      `/widgets/${WidgetTypes.SingleKPILastPeriod}/data`,
      graphConfig,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return mapFromKPIGraphData(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataLibraryReadResponse(
  link: ResourceLink,
  accessToken: string,
  signal?: AbortSignal,
): Promise<Widget61DTO> {
  try {
    const dto = new Widget61DTO();
    dto.link = mapToResourceLink(link, true);

    const ar = await http.post<Widget61DTO>(
      `/widgets/${WidgetTypes.LibraryReadResponse}/data`,
      dto,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataLibraryReadResponseAudiences(
  accessToken: string,
  signal?: AbortSignal,
): Promise<Widget61DTO> {
  try {
    const dto = new Widget61DTO();
    const ar = await http.post<Widget61DTO>(
      `/widgets/${WidgetTypes.LibraryReadResponseAudiences}/data`,
      dto,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataLibraryReadStats(
  viewMode: WidgetLibraryReadStatsConfigViewType,
  listId: number,
  accessToken: string,
  signal?: AbortSignal,
): Promise<Widget63DTO> {
  try {
    const dto = new Widget63DTO();
    dto.viewMode = viewMode;
    dto.listId = listId;

    const ar = await http.post<Widget63DTO>(
      `/widgets/${WidgetTypes.LibraryReadStats}/data`,
      dto,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetWidgetDataUserLinks(
  accessToken: string,
  cache: GlobalDataCache,
  signal?: AbortSignal,
): Promise<ResourceLink[]> {
  try {
    const ar = await http.get<ResourceLinkDTO[]>(
      `/widgets/${WidgetTypes.LibraryMyItems}/data`,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );

    return mapFromResourceLinks(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
